import { Dispatch, ElementType, forwardRef, Ref, SetStateAction } from "react";
import { Box } from "@mui/material";
import {
  RichTreeView,
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2Icon,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Props,
  TreeItem2Provider,
  TreeItem2Root,
  useTreeItem2Utils,
} from "@mui/x-tree-view";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2/useTreeItem2";
import useTablesHierarchy from "datasets/pages/tables/hooks/useTablesHierarchy";
import EmptyState from "~/components/EmptyState";
import Loading from "~/components/Loading";
import { TableAccessLevel } from "~/pages/datasets/types/enums/access-level";

interface CustomTreeItemProps extends TreeItem2Props {
  icon?: ElementType;
}

const TableTreeItem = forwardRef(function TableTreeItem(
  props: CustomTreeItemProps,
  ref: Ref<HTMLLIElement>
) {
  const { id, itemId, label, disabled, children, ...other } = props;

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  // TODO: Sort out `any` usage here and below
  const item = (publicAPI as any).getItem(itemId);
  const contentProps = getContentProps();

  const { status } = useTreeItem2Utils({
    itemId: props.itemId,
    children: props.children,
  });

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root {...getRootProps<any>(other)}>
        <TreeItem2Content
          {...contentProps}
          onClick={(event) => {
            // If the node is a leaf (not expandable), toggle the checkbox
            if (!status.expandable) {
              const checkboxProps = getCheckboxProps();
              if (checkboxProps.onChange) {
                checkboxProps.onChange(event as any);
              }
            }
            // Call any existing onClick handler from contentProps
            if (contentProps.onClick) {
              contentProps.onClick(event);
            }
          }}
          sx={{
            gap: 0.5,
          }}
        >
          {status.expandable ? (
            <>
              <TreeItem2IconContainer {...getIconContainerProps()} sx={{ color: "grey.600" }}>
                <TreeItem2Icon status={status} />
              </TreeItem2IconContainer>
              {item.icon && (
                <Box mr={0.5}>
                  <item.icon />
                </Box>
              )}
            </>
          ) : (
            <TreeItem2Checkbox {...getCheckboxProps()} size="small" />
          )}

          <TreeItem2Label {...getLabelProps()} sx={{ fontSize: 14 }} />
        </TreeItem2Content>
        <TreeItem2GroupTransition {...getGroupTransitionProps()} />
      </TreeItem2Root>
    </TreeItem2Provider>
  );
});

const TableTreeView = ({
  searchTerm,
  selectedItems,
  setSelectedItems,
  accessLevel,
}: {
  searchTerm: string;
  selectedItems: string[];
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  accessLevel?: TableAccessLevel;
}) => {
  const { data: tablesTree, isLoading } = useTablesHierarchy(searchTerm, accessLevel);

  if (tablesTree && !tablesTree.length) {
    return <EmptyState label="No tables found" />;
  }

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <RichTreeView
          multiSelect
          itemChildrenIndentation={20}
          checkboxSelection
          items={tablesTree ?? []}
          defaultExpandedItems={tablesTree?.[0] ? [tablesTree[0]?.id] : []}
          slots={{ item: TableTreeItem }}
          selectedItems={selectedItems}
          onItemSelectionToggle={(e, itemId, isSelected) => {
            if (isSelected) {
              setSelectedItems((prev) => Array.from(new Set([...prev, itemId])));
            } else {
              setSelectedItems((prev) => prev.filter((fqn) => fqn !== itemId));
            }
          }}
        />
      )}
    </>
  );
};

export default TableTreeView;
